import { useState, useEffect } from "react";
import { useAuth } from "@/hooks/useAuth";

interface ApiKey {
  id: number;
  name: string;
  key: string;
  prefix: string;
  permissions: ("send" | "history" | "contacts" | "stats")[];
  status: "active" | "inactive";
  lastUsed: string | null;
  createdAt: string;
  expiresAt: string | null;
  requestCount: number;
  env: "live" | "test";
}

interface DbApiKey {
  id: number;
  name: string;
  key_value: string;
  prefix: string;
  permissions: string[];
  status: "active" | "inactive";
  env: "live" | "test";
  last_used_at: string | null;
  created_at: string;
  expires_at: string | null;
  request_count: number;
}

const PERMISSION_LABELS: Record<string, { label: string; icon: string; color: string }> = {
  send: { label: "문자 발송", icon: "ri-send-plane-line", color: "text-violet-400" },
  history: { label: "발송 내역", icon: "ri-history-line", color: "text-sky-400" },
  contacts: { label: "주소록", icon: "ri-contacts-book-2-line", color: "text-emerald-400" },
  stats: { label: "통계", icon: "ri-bar-chart-2-line", color: "text-amber-400" },
};

const ALL_PERMISSIONS = ["send", "history", "contacts", "stats"] as const;

function formatDate(dateStr: string | null): string | null {
  if (!dateStr) return null;
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString("ko-KR", { year: "numeric", month: "2-digit", day: "2-digit" }).replace(/\.$/, "");
  } catch {
    return null;
  }
}

function formatDateTime(dateStr: string | null): string | null {
  if (!dateStr) return null;
  try {
    const d = new Date(dateStr);
    return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, "0")}.${String(d.getDate()).padStart(2, "0")} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  } catch {
    return null;
  }
}

function mapDbToApiKey(db: DbApiKey): ApiKey {
  return {
    id: db.id,
    name: db.name,
    key: db.key_value,
    prefix: db.prefix,
    permissions: (db.permissions || []).filter((p): p is "send" | "history" | "contacts" | "stats" =>
      ALL_PERMISSIONS.includes(p as (typeof ALL_PERMISSIONS)[number])
    ),
    status: db.status,
    env: db.env,
    lastUsed: formatDateTime(db.last_used_at),
    createdAt: formatDate(db.created_at) || "",
    expiresAt: formatDate(db.expires_at),
    requestCount: db.request_count || 0,
  };
}

export default function ApiKeyTab() {
  const { user, session } = useAuth();
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [revealedId, setRevealedId] = useState<number | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newKeyResult, setNewKeyResult] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  // Create form
  const [createForm, setCreateForm] = useState({
    name: "",
    permissions: ["send"] as ("send" | "history" | "contacts" | "stats")[],
    expiresAt: "",
    env: "live" as "live" | "test",
  });
  const [createErrors, setCreateErrors] = useState<{ name?: string; permissions?: string }>();

  const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;
  const SUPABASE_ANON_KEY = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as string;

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2500);
  };

  const fetchKeys = async () => {
    if (!user || !session) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/manage-api-keys`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          apikey: SUPABASE_ANON_KEY,
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ action: "list", user_id: user.id }),
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      setKeys((data.api_keys || []).map(mapDbToApiKey));
    } catch (err: any) {
      setError(err.message || "API 키를 불러오지 못했습니다.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchKeys();
  }, [user?.id, session?.access_token]);

  const handleCopy = (text: string, id: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
      showToast("클립보드에 복사되었습니다");
    });
  };

  const handleToggleStatus = async (id: number) => {
    if (!user || !session) return;
    setActionLoading(true);
    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/manage-api-keys`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          apikey: SUPABASE_ANON_KEY,
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ action: "toggle", user_id: user.id, key_id: id }),
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      setKeys((prev) =>
        prev.map((k) => (k.id === id ? mapDbToApiKey(data.api_key) : k))
      );
      const key = keys.find((k) => k.id === id);
      showToast(key?.status === "active" ? "API 키가 비활성화되었습니다" : "API 키가 활성화되었습니다");
    } catch (err: any) {
      showToast(err.message || "상태 변경에 실패했습니다");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!user || !session) return;
    setActionLoading(true);
    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/manage-api-keys`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          apikey: SUPABASE_ANON_KEY,
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ action: "delete", user_id: user.id, key_id: id }),
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      setKeys((prev) => prev.filter((k) => k.id !== id));
      setDeleteConfirmId(null);
      showToast("API 키가 삭제되었습니다");
    } catch (err: any) {
      showToast(err.message || "삭제에 실패했습니다");
    } finally {
      setActionLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!user || !session) return;
    const errors: { name?: string; permissions?: string } = {};
    if (!createForm.name.trim()) errors.name = "키 이름을 입력해주세요";
    if (createForm.permissions.length === 0) errors.permissions = "최소 1개의 권한을 선택해주세요";
    if (Object.keys(errors).length > 0) {
      setCreateErrors(errors);
      return;
    }

    setActionLoading(true);
    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/manage-api-keys`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          apikey: SUPABASE_ANON_KEY,
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          action: "create",
          user_id: user.id,
          name: createForm.name,
          permissions: createForm.permissions,
          expires_at: createForm.expiresAt || null,
          env: createForm.env,
        }),
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      const created = mapDbToApiKey(data.api_key);
      setKeys((prev) => [created, ...prev]);
      setNewKeyResult(created.key);
      setShowCreateModal(false);
      setCreateForm({ name: "", permissions: ["send"], expiresAt: "", env: "live" });
      setCreateErrors({});
    } catch (err: any) {
      showToast(err.message || "발급에 실패했습니다");
    } finally {
      setActionLoading(false);
    }
  };

  const togglePermission = (perm: "send" | "history" | "contacts" | "stats") => {
    setCreateForm((p) => ({
      ...p,
      permissions: p.permissions.includes(perm)
        ? p.permissions.filter((x) => x !== perm)
        : [...p.permissions, perm],
    }));
    setCreateErrors((p) => ({ ...p, permissions: undefined }));
  };

  const formatCount = (n: number) => (n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n));

  const totalRequests = keys.reduce((sum, k) => sum + k.requestCount, 0);
  const activeKeys = keys.filter((k) => k.status === "active").length;
  const inactiveKeys = keys.filter((k) => k.status === "inactive").length;
  const testKeys = keys.filter((k) => k.env === "test").length;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <i className="ri-loader-4-line animate-spin text-violet-400 text-3xl"></i>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-white font-bold text-base md:text-lg">API 키 관리</h2>
          <p className="text-gray-500 text-xs md:text-sm mt-0.5">외부 서비스 연동을 위한 API 키를 발급하고 관리합니다</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          disabled={actionLoading}
          className="flex items-center justify-center gap-2 bg-gradient-to-r from-violet-600 to-pink-600 hover:from-violet-500 hover:to-pink-500 text-white px-4 py-2.5 rounded-xl text-sm font-semibold cursor-pointer whitespace-nowrap transition-all hover:scale-105 disabled:opacity-50"
        >
          <i className="ri-key-2-line text-base"></i>
          새 API 키 발급
        </button>
      </div>

      {error && (
        <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
          <i className="ri-error-warning-line"></i>
          {error}
          <button onClick={fetchKeys} className="ml-auto text-red-400 hover:text-red-300 text-xs underline cursor-pointer">
            다시 시도
          </button>
        </div>
      )}

      {/* New key result banner */}
      {newKeyResult && (
        <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <div className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5">
              <i className="ri-checkbox-circle-line text-emerald-400 text-base"></i>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-emerald-300 text-sm font-semibold mb-1">API 키가 발급되었습니다!</p>
              <p className="text-emerald-400/70 text-xs mb-3">이 키는 지금만 확인할 수 있습니다. 안전한 곳에 저장해주세요.</p>
              <div className="flex items-center gap-2 bg-black/30 rounded-lg px-3 py-2">
                <code className="text-emerald-300 text-xs font-mono flex-1 break-all">{newKeyResult}</code>
                <button
                  onClick={() => handleCopy(newKeyResult, "new")}
                  className="w-7 h-7 flex items-center justify-center text-emerald-400 hover:text-white cursor-pointer transition-colors shrink-0"
                >
                  <i className={`${copiedId === "new" ? "ri-checkbox-circle-line" : "ri-clipboard-line"} text-sm`}></i>
                </button>
              </div>
            </div>
            <button onClick={() => setNewKeyResult(null)} className="w-6 h-6 flex items-center justify-center text-gray-500 hover:text-white cursor-pointer transition-colors shrink-0">
              <i className="ri-close-line text-sm"></i>
            </button>
          </div>
        </div>
      )}

      {/* API docs link */}
      <div className="flex items-center gap-3 bg-white/[0.03] border border-white/[0.07] rounded-xl p-4">
        <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-violet-500/20 shrink-0">
          <i className="ri-book-2-line text-violet-400 text-lg"></i>
        </div>
        <div className="flex-1">
          <p className="text-white text-sm font-semibold">API 문서</p>
          <p className="text-gray-500 text-xs">REST API를 통해 문자 발송, 주소록 관리 등을 자동화할 수 있습니다</p>
        </div>
        <a
          href="#"
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/[0.05] border border-white/[0.08] text-gray-300 hover:text-white text-xs font-medium cursor-pointer whitespace-nowrap transition-all"
        >
          문서 보기
          <i className="ri-external-link-line text-xs"></i>
        </a>
      </div>

      {/* Key list */}
      <div className="space-y-3">
        {keys.length === 0 ? (
          <div className="text-center py-12 bg-white/[0.02] border border-white/[0.06] rounded-2xl">
            <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3 rounded-full bg-white/[0.05]">
              <i className="ri-key-2-line text-gray-600 text-xl"></i>
            </div>
            <p className="text-gray-400 text-sm font-medium">등록된 API 키가 없습니다</p>
            <p className="text-gray-600 text-xs mt-1">새 API 키를 발급하여 외부 서비스와 연동해보세요</p>
          </div>
        ) : (
          keys.map((apiKey) => (
            <div
              key={apiKey.id}
              className={`bg-white/[0.03] border rounded-2xl p-4 md:p-5 transition-all ${
                apiKey.status === "active" ? "border-white/[0.08] hover:border-white/[0.12]" : "border-white/[0.04] opacity-60"
              }`}
            >
              <div className="flex flex-col sm:flex-row sm:items-start gap-3 md:gap-4">
                {/* Icon */}
                <div className={`w-10 h-10 flex items-center justify-center rounded-xl shrink-0 ${
                  apiKey.status === "active" ? "bg-violet-500/20" : "bg-white/[0.05]"
                }`}>
                  <i className={`ri-key-2-line text-lg ${apiKey.status === "active" ? "text-violet-400" : "text-gray-600"}`}></i>
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="text-white font-semibold text-sm">{apiKey.name}</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${
                      apiKey.status === "active"
                        ? "bg-emerald-500/10 border border-emerald-500/20 text-emerald-400"
                        : "bg-white/[0.05] border border-white/[0.08] text-gray-500"
                    }`}>
                      {apiKey.status === "active" ? "활성" : "비활성"}
                    </span>
                    {apiKey.env === "test" && (
                      <span className="px-2 py-0.5 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-400 text-xs font-medium whitespace-nowrap">TEST</span>
                    )}
                  </div>

                  {/* Key display */}
                  <div className="flex items-center gap-2 bg-black/20 rounded-lg px-3 py-1.5 mb-3 w-fit max-w-full min-w-0">
                    <code className="text-gray-400 text-xs font-mono truncate max-w-[200px] sm:max-w-[280px]">
                      {revealedId === apiKey.id ? apiKey.key : `${apiKey.prefix}${"\u2022".repeat(20)}`}
                    </code>
                    <button
                      onClick={() => setRevealedId(revealedId === apiKey.id ? null : apiKey.id)}
                      className="w-5 h-5 flex items-center justify-center text-gray-600 hover:text-gray-300 cursor-pointer transition-colors shrink-0"
                    >
                      <i className={`${revealedId === apiKey.id ? "ri-eye-off-line" : "ri-eye-line"} text-xs`}></i>
                    </button>
                    <button
                      onClick={() => handleCopy(apiKey.key, String(apiKey.id))}
                      className="w-5 h-5 flex items-center justify-center text-gray-600 hover:text-gray-300 cursor-pointer transition-colors shrink-0"
                    >
                      <i className={`${copiedId === String(apiKey.id) ? "ri-checkbox-circle-line text-emerald-400" : "ri-clipboard-line"} text-xs`}></i>
                    </button>
                  </div>

                  {/* Permissions */}
                  <div className="flex items-center gap-1.5 flex-wrap mb-2">
                    {apiKey.permissions.map((perm) => {
                      const cfg = PERMISSION_LABELS[perm];
                      return (
                        <span key={perm} className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/[0.05] border border-white/[0.07] text-xs whitespace-nowrap">
                          <i className={`${cfg.icon} ${cfg.color} text-xs`}></i>
                          <span className="text-gray-400">{cfg.label}</span>
                        </span>
                      );
                    })}
                  </div>

                  {/* Meta */}
                  <div className="flex items-center gap-3 text-xs text-gray-600 flex-wrap">
                    <span>생성일 {apiKey.createdAt}</span>
                    {apiKey.expiresAt && <span>만료일 {apiKey.expiresAt}</span>}
                    {apiKey.lastUsed && <span>마지막 사용 {apiKey.lastUsed}</span>}
                    <span className="text-gray-500">{formatCount(apiKey.requestCount)} 요청</span>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={() => handleToggleStatus(apiKey.id)}
                    disabled={actionLoading}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium cursor-pointer whitespace-nowrap transition-all border disabled:opacity-50 ${
                      apiKey.status === "active"
                        ? "bg-white/[0.05] border-white/[0.08] text-gray-400 hover:text-white"
                        : "bg-emerald-500/10 border-emerald-500/20 text-emerald-400 hover:bg-emerald-500/20"
                    }`}
                  >
                    {apiKey.status === "active" ? "비활성화" : "활성화"}
                  </button>
                  <button
                    onClick={() => setDeleteConfirmId(apiKey.id)}
                    disabled={actionLoading}
                    className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-600 hover:text-red-400 hover:bg-red-500/10 cursor-pointer transition-all disabled:opacity-50"
                  >
                    <i className="ri-delete-bin-line text-sm"></i>
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Usage stats */}
      <div className="bg-white/[0.03] border border-white/[0.07] rounded-2xl p-5">
        <h3 className="text-white font-semibold text-sm mb-4 flex items-center gap-2">
          <div className="w-5 h-5 flex items-center justify-center">
            <i className="ri-bar-chart-2-line text-violet-400 text-base"></i>
          </div>
          API 사용 현황
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 md:gap-4">
          {[
            { label: "총 요청", value: formatCount(totalRequests), icon: "ri-arrow-up-down-line", color: "text-violet-400" },
            { label: "활성 키", value: String(activeKeys), icon: "ri-checkbox-circle-line", color: "text-emerald-400" },
            { label: "비활성", value: String(inactiveKeys), icon: "ri-close-circle-line", color: "text-red-400" },
            { label: "TEST 키", value: String(testKeys), icon: "ri-flask-line", color: "text-amber-400" },
          ].map((stat) => (
            <div key={stat.label} className="text-center">
              <div className="w-8 h-8 flex items-center justify-center mx-auto mb-1.5">
                <i className={`${stat.icon} text-xl ${stat.color}`}></i>
              </div>
              <p className="text-white font-bold text-lg">{stat.value}</p>
              <p className="text-gray-500 text-xs">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Create modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center p-0 md:p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => !actionLoading && setShowCreateModal(false)}></div>
          <div className="relative bg-[#0d0d1a] border border-white/[0.1] rounded-t-2xl md:rounded-2xl p-5 md:p-6 w-full max-w-md max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-white font-bold text-base">새 API 키 발급</h3>
              <button onClick={() => !actionLoading && setShowCreateModal(false)} className="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-white cursor-pointer transition-colors">
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>

            <div className="space-y-4">
              {/* Name */}
              <div>
                <label className="block text-gray-400 text-xs font-medium mb-1.5">키 이름 <span className="text-red-400">*</span></label>
                <input
                  type="text"
                  value={createForm.name}
                  onChange={(e) => { setCreateForm((p) => ({ ...p, name: e.target.value })); setCreateErrors((p) => ({ ...p, name: undefined })); }}
                  placeholder="예: 프로덕션 서버, 개발 환경"
                  disabled={actionLoading}
                  className={`w-full bg-white/[0.04] border rounded-xl px-4 py-2.5 text-white text-sm placeholder-gray-600 focus:outline-none transition-all disabled:opacity-50 ${
                    createErrors.name ? "border-red-500/50" : "border-white/[0.08] focus:border-violet-500/50"
                  }`}
                />
                {createErrors.name && <p className="text-red-400 text-xs mt-1">{createErrors.name}</p>}
              </div>

              {/* Environment */}
              <div>
                <label className="block text-gray-400 text-xs font-medium mb-1.5">환경</label>
                <div className="flex gap-2">
                  {(["live", "test"] as const).map((env) => (
                    <button
                      key={env}
                      onClick={() => setCreateForm((p) => ({ ...p, env }))}
                      disabled={actionLoading}
                      className={`flex-1 py-2 rounded-xl text-sm font-medium cursor-pointer whitespace-nowrap transition-all border disabled:opacity-50 ${
                        createForm.env === env
                          ? "bg-violet-500/20 border-violet-500/40 text-violet-300"
                          : "bg-white/[0.04] border-white/[0.08] text-gray-400 hover:text-white"
                      }`}
                    >
                      {env === "live" ? "프로덕션 (live)" : "테스트 (test)"}
                    </button>
                  ))}
                </div>
              </div>

              {/* Permissions */}
              <div>
                <label className="block text-gray-400 text-xs font-medium mb-1.5">권한 <span className="text-red-400">*</span></label>
                <div className="grid grid-cols-2 gap-2">
                  {ALL_PERMISSIONS.map((perm) => {
                    const cfg = PERMISSION_LABELS[perm];
                    const selected = createForm.permissions.includes(perm);
                    return (
                      <button
                        key={perm}
                        onClick={() => !actionLoading && togglePermission(perm)}
                        className={`flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm cursor-pointer whitespace-nowrap transition-all border disabled:opacity-50 ${
                          selected
                            ? "bg-violet-500/20 border-violet-500/40 text-white"
                            : "bg-white/[0.04] border-white/[0.08] text-gray-400 hover:text-white"
                        }`}
                      >
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className={`${cfg.icon} text-sm ${selected ? cfg.color : "text-current"}`}></i>
                        </div>
                        {cfg.label}
                        {selected && <i className="ri-checkbox-circle-fill text-violet-400 text-sm ml-auto"></i>}
                      </button>
                    );
                  })}
                </div>
                {createErrors.permissions && <p className="text-red-400 text-xs mt-1">{createErrors.permissions}</p>}
              </div>

              {/* Expiry */}
              <div>
                <label className="block text-gray-400 text-xs font-medium mb-1.5">만료일 (선택)</label>
                <input
                  type="date"
                  value={createForm.expiresAt}
                  onChange={(e) => setCreateForm((p) => ({ ...p, expiresAt: e.target.value }))}
                  disabled={actionLoading}
                  className="w-full bg-white/[0.04] border border-white/[0.08] rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-violet-500/50 transition-all disabled:opacity-50"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button onClick={() => !actionLoading && setShowCreateModal(false)} disabled={actionLoading} className="flex-1 py-3 rounded-xl bg-white/[0.06] text-gray-300 hover:text-white text-sm font-semibold cursor-pointer whitespace-nowrap transition-all disabled:opacity-50">취소</button>
              <button onClick={handleCreate} disabled={actionLoading} className="flex-1 py-3 rounded-xl bg-gradient-to-r from-violet-600 to-pink-600 hover:from-violet-500 hover:to-pink-500 text-white text-sm font-bold cursor-pointer whitespace-nowrap transition-all disabled:opacity-50 flex items-center justify-center gap-2">
                {actionLoading && <i className="ri-loader-4-line animate-spin text-sm"></i>}
                <i className="ri-key-2-line mr-1.5"></i>
                발급
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirm */}
      {deleteConfirmId !== null && (
        <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center p-0 md:p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => !actionLoading && setDeleteConfirmId(null)}></div>
          <div className="relative bg-[#0d0d1a] border border-white/[0.1] rounded-t-2xl md:rounded-2xl p-5 md:p-6 w-full md:max-w-sm text-center">
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-red-500/20 mx-auto mb-4">
              <i className="ri-key-2-line text-red-400 text-2xl"></i>
            </div>
            <h3 className="text-white font-bold text-base mb-2">API 키를 삭제할까요?</h3>
            <p className="text-gray-400 text-sm mb-6">삭제된 키는 즉시 무효화되며 복구할 수 없습니다.</p>
            <div className="flex gap-3">
              <button onClick={() => !actionLoading && setDeleteConfirmId(null)} disabled={actionLoading} className="flex-1 py-2.5 rounded-xl bg-white/[0.06] text-gray-300 hover:text-white text-sm font-semibold cursor-pointer whitespace-nowrap transition-all disabled:opacity-50">취소</button>
              <button onClick={() => handleDelete(deleteConfirmId)} disabled={actionLoading} className="flex-1 py-2.5 rounded-xl bg-red-500/20 border border-red-500/30 text-red-400 hover:bg-red-500/30 text-sm font-bold cursor-pointer whitespace-nowrap transition-all disabled:opacity-50 flex items-center justify-center gap-2">
                {actionLoading && <i className="ri-loader-4-line animate-spin text-sm"></i>}
                삭제
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-20 md:bottom-6 right-4 md:right-6 z-[60] flex items-center gap-2.5 px-4 py-3 rounded-xl bg-emerald-500/20 border border-emerald-500/30 text-emerald-300">
          <i className="ri-checkbox-circle-line text-base"></i>
          <span className="text-sm font-medium">{toast}</span>
        </div>
      )}
    </div>
  );
}